// The generate-fuel-plan handler.
//
// This is the frontend equivalent of a command handler: the component fires one
// intent (`submitFuelPlan`) and this hook orchestrates the flow - build the
// request (services), call the API (api.ts), email ops if generation runs long
// (messaging), and normalize the response (services). The long-running request
// is never discarded: the timeout only triggers the ops email as a side effect,
// while the request keeps running and its result is always returned. It has to
// be a hook, not a plain service, because it depends on TanStack Query
// mutations, which can only be resolved inside the React tree. It owns no UI
// state: it returns a result the component maps onto its own state.

import { useAuth0 } from "@auth0/auth0-react";
import { useGenerateFuelPlanPro } from "./api";
import { useSendEmail } from "./messaging";
import {
  buildFuelPlanProParams,
  buildTimeoutEmailPayload,
  normalizeProRoutes,
  type FuelPlanFormData,
  type NormalizedFuelPlanRoute,
} from "./services";

const COMPANY_CLAIM = "https://ifuelsmart.com/company";
const TIMEOUT_MS = 10_000;

export type SubmitFuelPlanResult =
  // Business success: normalized Pro routes ranked by the optimizer.
  | { status: "success"; fuelPlan: NormalizedFuelPlanRoute[] }
  // Optimizer returned a 200 with an `{ error }` payload (e.g. not enough fuel).
  | { status: "error"; error: string }
  // Request threw (network/unexpected); surfaced nothing to the user, as before.
  | { status: "failed" };

export function useSubmitFuelPlan() {
  const { user } = useAuth0();
  const { mutateAsync: getFuelPlanPro } = useGenerateFuelPlanPro();
  const { mutateAsync: sendEmail } = useSendEmail();

  async function runPro(formData: FuelPlanFormData, customer: string): Promise<SubmitFuelPlanResult> {
    try {
      const data = await getFuelPlanPro(buildFuelPlanProParams(formData, customer));
      if (!Array.isArray(data) && "error" in data) {
        return { status: "error", error: data.error };
      }
      return { status: "success", fuelPlan: normalizeProRoutes(data, formData) };
    } catch {
      return { status: "failed" };
    }
  }

  return async function submitFuelPlan(formData: FuelPlanFormData): Promise<SubmitFuelPlanResult> {
    const customer = user?.[COMPANY_CLAIM] as string;

    // If generation runs long, email ops as a fire-and-forget side effect. The
    // request is never cancelled: it keeps running and its result is always
    // returned, so the plan still populates whenever it eventually resolves.
    const timer = setTimeout(() => {
      sendEmail(buildTimeoutEmailPayload(formData, customer)).catch((error) => {
        console.error("An error occurred:", error);
      });
    }, TIMEOUT_MS);

    try {
      return await runPro(formData, customer);
    } finally {
      clearTimeout(timer);
    }
  };
}
